import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { supabase } from '@/services/supabaseClient';
import type {
  ClaimNearbyAlertResult,
  MatchNotificationType,
  NearbyAlertStatus,
} from '@/types/database';
import { useUserStore } from './useUserStore';

export interface MatchNotificationItem {
  id: string;
  receiverId: string;
  senderId: string;
  senderUsername: string;
  senderAvatarUrl: string | null;
  matchGroupId: string;
  type: MatchNotificationType;
  payload: Record<string, unknown>;
  timestamp: string;
  readAt: string | null;
}

export interface NearbyAlertItem {
  id: string;
  receiverId: string;
  otherUserId: string;
  otherUsername: string;
  otherAvatarUrl: string | null;
  proposalKey: string;
  requestedInventoryIds: string[];
  offeredInventoryIds: string[];
  distanceKm: number;
  status: NearbyAlertStatus;
  matchGroupId: string | null;
  readAt: string | null;
  expiresAt: string;
  createdAt: string;
}

const FREE_ACTIVE_MATCH_CAP_ERROR_CODE = 'FREE_ACTIVE_MATCH_CAP_REACHED';

export type NearbyClaimDomainReason = 'free_cap_reached';

export type NearbyClaimDomainError = Error & {
  reason: NearbyClaimDomainReason;
  raw?: unknown;
};

function normalizeType(value: unknown): MatchNotificationType {
  const raw = String(value ?? '');
  if (
    raw === 'new_match'
    || raw === 'trade_proposed'
    || raw === 'trade_accepted'
    || raw === 'trade_cancelled'
    || raw === 'review_prompt'
  ) {
    return raw;
  }
  return 'new_match';
}

function normalizeNearbyStatus(value: unknown): NearbyAlertStatus {
  const raw = String(value ?? '');
  if (raw === 'pending' || raw === 'claimed' || raw === 'superseded' || raw === 'expired') {
    return raw;
  }
  return 'pending';
}

function extractErrorText(error: unknown): string {
  if (!error) return '';
  const sources: unknown[] = [
    (error as any)?.message,
    (error as any)?.error_description,
    (error as any)?.details,
    (error as any)?.hint,
    error,
  ];
  return sources
    .map((entry) => (typeof entry === 'string' ? entry : ''))
    .join(' | ')
    .toUpperCase();
}

function mapNearbyClaimDomainError(error: unknown): NearbyClaimDomainError | null {
  const text = extractErrorText(error);
  if (!text.includes(FREE_ACTIVE_MATCH_CAP_ERROR_CODE)) return null;
  const domainError = new Error(FREE_ACTIVE_MATCH_CAP_ERROR_CODE) as NearbyClaimDomainError;
  domainError.reason = 'free_cap_reached';
  domainError.raw = error;
  return domainError;
}

export const useNotificationStore = defineStore('notifications', () => {
  const items = ref<MatchNotificationItem[]>([]);
  const nearbyAlerts = ref<NearbyAlertItem[]>([]);
  const loading = ref(false);

  let matchChannel: ReturnType<typeof supabase.channel> | null = null;
  let nearbyChannel: ReturnType<typeof supabase.channel> | null = null;

  const pendingNearbyCount = computed(
    () => nearbyAlerts.value.filter((a) => a.status === 'pending').length
  );
  const unreadCount = computed(() => items.value.length + pendingNearbyCount.value);

  async function resolveUserId(): Promise<string | null> {
    const userStore = useUserStore();
    return (
      userStore.user?.id
      ?? (await supabase.auth.getSession()).data.session?.user?.id
      ?? null
    );
  }

  async function loadMatchUnread(userId: string, limit: number): Promise<void> {
    const { data, error } = await supabase
      .from('match_notifications')
      .select(`
        id,
        receiver_id,
        sender_id,
        match_group_id,
        type,
        payload,
        created_at,
        read_at,
        sender:profiles!match_notifications_sender_id_fkey(id, username, avatar_url)
      `)
      .eq('receiver_id', userId)
      .is('read_at', null)
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) throw error;

    items.value = (data ?? []).map((row: any) => ({
      id: String(row.id),
      receiverId: String(row.receiver_id),
      senderId: String(row.sender_id),
      senderUsername: String(row.sender?.username ?? 'Unknown'),
      senderAvatarUrl: row.sender?.avatar_url ?? null,
      matchGroupId: String(row.match_group_id),
      type: normalizeType(row.type),
      payload: row.payload && typeof row.payload === 'object'
        ? row.payload as Record<string, unknown>
        : {},
      timestamp: String(row.created_at),
      readAt: row.read_at ? String(row.read_at) : null,
    }));
  }

  async function loadNearbyPending(limit = 20): Promise<void> {
    const userId = await resolveUserId();
    if (!userId) {
      nearbyAlerts.value = [];
      return;
    }
    const { data, error } = await supabase
      .from('nearby_trade_alerts')
      .select(`
        id,
        receiver_id,
        other_user_id,
        proposal_key,
        requested_inventory_ids,
        offered_inventory_ids,
        distance_km,
        status,
        match_group_id,
        read_at,
        expires_at,
        created_at,
        other:profiles!nearby_trade_alerts_other_user_id_fkey(id, username, avatar_url)
      `)
      .eq('receiver_id', userId)
      .eq('status', 'pending')
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) throw error;

    nearbyAlerts.value = (data ?? []).map((row: any) => ({
      id: String(row.id),
      receiverId: String(row.receiver_id),
      otherUserId: String(row.other_user_id),
      otherUsername: String(row.other?.username ?? 'Unknown'),
      otherAvatarUrl: row.other?.avatar_url ?? null,
      proposalKey: String(row.proposal_key),
      requestedInventoryIds: Array.isArray(row.requested_inventory_ids)
        ? row.requested_inventory_ids.map(String)
        : [],
      offeredInventoryIds: Array.isArray(row.offered_inventory_ids)
        ? row.offered_inventory_ids.map(String)
        : [],
      distanceKm: Number(row.distance_km ?? 0),
      status: normalizeNearbyStatus(row.status),
      matchGroupId: row.match_group_id ? String(row.match_group_id) : null,
      readAt: row.read_at ? String(row.read_at) : null,
      expiresAt: String(row.expires_at),
      createdAt: String(row.created_at),
    }));
  }

  async function loadUnread(limit = 50): Promise<void> {
    const userId = await resolveUserId();
    if (!userId) {
      items.value = [];
      nearbyAlerts.value = [];
      return;
    }
    loading.value = true;
    try {
      await Promise.all([
        loadMatchUnread(userId, limit),
        loadNearbyPending(),
      ]);
    } finally {
      loading.value = false;
    }
  }

  function subscribeRealtime(): void {
    const userStore = useUserStore();
    const uid = userStore.user?.id;
    if (!uid) return;

    if (!matchChannel) {
      matchChannel = supabase
        .channel(`match-notifications-${uid}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'match_notifications',
            filter: `receiver_id=eq.${uid}`,
          },
          () => { void loadUnread().catch((e) => console.error('[notifications insert]', e)); }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'match_notifications',
            filter: `receiver_id=eq.${uid}`,
          },
          () => { void loadUnread().catch((e) => console.error('[notifications update]', e)); }
        )
        .subscribe();
    }

    if (!nearbyChannel) {
      nearbyChannel = supabase
        .channel(`nearby-alerts-${uid}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'nearby_trade_alerts',
            filter: `receiver_id=eq.${uid}`,
          },
          () => {
            void loadNearbyPending().catch((e) => console.error('[nearby alerts insert]', e));
            try {
              window.dispatchEvent(new CustomEvent('nearby-alert:new'));
            } catch {
              /* SSR guard */
            }
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'nearby_trade_alerts',
            filter: `receiver_id=eq.${uid}`,
          },
          () => { void loadNearbyPending().catch((e) => console.error('[nearby alerts update]', e)); }
        )
        .subscribe();
    }
  }

  function unsubscribeRealtime(): void {
    if (matchChannel) {
      supabase.removeChannel(matchChannel);
      matchChannel = null;
    }
    if (nearbyChannel) {
      supabase.removeChannel(nearbyChannel);
      nearbyChannel = null;
    }
    items.value = [];
    nearbyAlerts.value = [];
  }

  function resubscribeRealtime(): void {
    unsubscribeRealtime();
    subscribeRealtime();
    void loadUnread().catch((e) => console.error('[notifications refresh]', e));
  }

  async function markAsRead(notificationId: string): Promise<void> {
    const id = String(notificationId ?? '').trim();
    if (!id) return;
    const userId = await resolveUserId();
    if (!userId) return;

    const { error } = await supabase
      .from('match_notifications')
      .update({ read_at: new Date().toISOString() })
      .eq('id', id)
      .eq('receiver_id', userId)
      .is('read_at', null);
    if (error) throw error;

    await loadUnread();
  }

  async function markAllAsRead(): Promise<void> {
    const snapshot = [...items.value];
    if (!snapshot.length) return;
    const userId = await resolveUserId();
    if (!userId) return;

    items.value = [];

    const { error } = await supabase
      .from('match_notifications')
      .update({ read_at: new Date().toISOString() })
      .eq('receiver_id', userId)
      .is('read_at', null);

    if (error) {
      items.value = snapshot;
      throw error;
    }
  }

  async function markAsReadByMatchGroup(
    matchGroupId: string,
    type?: MatchNotificationType
  ): Promise<void> {
    const groupId = String(matchGroupId ?? '').trim();
    if (!groupId) return;
    const userId = await resolveUserId();
    if (!userId) return;

    let query = supabase
      .from('match_notifications')
      .update({ read_at: new Date().toISOString() })
      .eq('receiver_id', userId)
      .eq('match_group_id', groupId)
      .is('read_at', null);
    if (type) query = query.eq('type', type);
    const { error } = await query;
    if (error) throw error;

    await loadUnread();
  }

  async function markNearbyAlertAsRead(alertId: string): Promise<void> {
    const id = String(alertId ?? '').trim();
    if (!id) return;
    const userId = await resolveUserId();
    if (!userId) return;

    const { error } = await supabase
      .from('nearby_trade_alerts')
      .update({ read_at: new Date().toISOString() })
      .eq('id', id)
      .eq('receiver_id', userId)
      .is('read_at', null);
    if (error) throw error;

    await loadNearbyPending();
  }

  async function claimNearbyAlert(alertId: string): Promise<ClaimNearbyAlertResult> {
    const id = String(alertId ?? '').trim();
    if (!id) {
      return { match_group_id: null, already_claimed: false, free_cap_blocked: false };
    }

    const { data, error } = await supabase.rpc('claim_nearby_trade_alert', {
      p_alert_id: id,
    });
    if (error) {
      const mapped = mapNearbyClaimDomainError(error);
      if (mapped) throw mapped;
      throw error;
    }

    const row = Array.isArray(data) ? data[0] : data;
    const result: ClaimNearbyAlertResult = {
      match_group_id: row?.match_group_id ?? null,
      already_claimed: !!row?.already_claimed,
      free_cap_blocked: !!row?.free_cap_blocked,
    };

    await loadNearbyPending();
    return result;
  }

  return {
    items,
    nearbyAlerts,
    loading,
    unreadCount,
    pendingNearbyCount,
    loadUnread,
    loadNearbyPending,
    subscribeRealtime,
    unsubscribeRealtime,
    resubscribeRealtime,
    markAsRead,
    markAllAsRead,
    markAsReadByMatchGroup,
    markNearbyAlertAsRead,
    claimNearbyAlert,
  };
});
